import { ApiQueryParams, DefaultQueryParams, PaginatedResponse } from '@frontend/api-utils';
import { AllowanceClient, BudgetUseClient } from '@frontend/budget/api';
import { BudgetAllowance, BudgetType, BudgetUse } from '@frontend/budget/types';
import { Application, Feature } from '@frontend/functionality/types';
import { Logger } from '@frontend/logger';
import { ProductClient } from '@frontend/product/api';
import { IProduct, ProductType } from '@frontend/product/types';
import { useMemo, useState } from 'react';

import { useIdentification, useScanners } from '../../../hooks';
import { useLicenseRepository } from '../../../utils/license-repository';
import { useScreen } from '../useScreen';

interface ViewProps {
    isLoading: boolean;
    setIsLoading: (isLoading: boolean) => void;
    fetchData: (queryParams?: ApiQueryParams<DefaultQueryParams>) => Promise<PaginatedResponse<BudgetUse & BudgetAllowance>>;
    onClick: (object: BudgetUse & BudgetAllowance) => void;
}

const getNewestBudgetUseByProductId = (budgetUses: BudgetUse[]): BudgetUse[] => {
    const newestByProductId = new Map<string, BudgetUse>();

    budgetUses.forEach((budgetUse) => {
        const existing = newestByProductId.get(budgetUse.product_id);
        if (!existing) {
            newestByProductId.set(budgetUse.product_id, budgetUse);
            return;
        }

        const currentTimestamp = Date.parse(budgetUse.update_timestamp || budgetUse.creation_timestamp);
        const existingTimestamp = Date.parse(existing.update_timestamp || existing.creation_timestamp);
        if (currentTimestamp > existingTimestamp) {
            newestByProductId.set(budgetUse.product_id, budgetUse);
        }
    });

    return Array.from(newestByProductId.values());
};

const useReturnOverview = (): ViewProps => {
    const { functionalities } = useLicenseRepository();
    const { wizardData, updateWizardData, onSubmit, navigationState } = useScreen();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const { userOverwrite } = wizardData;

    const canReturnVendingProducts = useMemo(() => {
        return functionalities.some((f) => f.feature === Feature.RETURN_TRANSACTION && f.application === Application.VENDING);
    }, [functionalities]);

    useScanners({
        id: 'RETURN_OVERVIEW',
        onDeviceSensorReceived: (data) => {
            setIsLoading(true);
        }
    });
    useIdentification({
        id: 'RETURN_OVERVIEW',
        onNoMatchingIdentificationFound: () => {
            setIsLoading(false);
        },
        onUnlinkedIdentificationFound: () => {
            setIsLoading(false);
        },
        onEquipmentIdentified: () => {
            setIsLoading(false);
        },
        onProductIdentified: async (product) => {
            try {
                if (!canReturnVendingProducts) return;
                if (product.type === ProductType.EQUIPMENT) return;

                const uses = await BudgetUseClient.fetchBudgetUses({
                    product_id: product.id,
                    user_id: userOverwrite?.id ?? navigationState.user?.id
                });
                if (uses.results.length === 0) return;

                const allowances = await AllowanceClient.postGetUserAllowancesV2(
                    uses.results.map((bu) => bu.product_id),
                    {
                        spot_id: navigationState.spot?.id ?? '',
                        type: BudgetType.REPLENISHMENT,
                        user_id: userOverwrite?.id ?? undefined
                    },
                    navigationState.user?.auth
                );
                const filteredUses = uses.results.filter((bu) => allowances[bu.product_id].allowed);
                if (filteredUses.length > 0) {
                    await onSelectProduct(product);
                }
            } finally {
                setIsLoading(false);
            }
        }
    });

    const onSelectProduct = async (product?: IProduct) => {
        setIsLoading(true);
        try {
            if (!canReturnVendingProducts || !product || product.type === ProductType.EQUIPMENT) return;

            updateWizardData({
                product,
                equipment: null
            });
            onSubmit();
        } finally {
            setIsLoading(false);
        }
    };

    const onClick = async (object: BudgetUse & BudgetAllowance) => {
        if (!object.allowed) return;

        const product = await ProductClient.fetchProduct(object.account_id, object.product_id);
        if (product.type === ProductType.EQUIPMENT) return;

        updateWizardData({ budgetUse: object });
        onSelectProduct(product);
    };

    const fetchData = async (queryParams?: ApiQueryParams<DefaultQueryParams>): Promise<PaginatedResponse<BudgetUse & BudgetAllowance>> => {
        if (!canReturnVendingProducts) return { count: 0, results: [] };

        try {
            const budgetUses = await BudgetUseClient.fetchBudgetUses({
                ...queryParams,
                user_id: userOverwrite?.id ?? navigationState.user?.id
            });
            const newestBudgetUses = getNewestBudgetUseByProductId(budgetUses.results);
            const returnableBudgetUses = (
                await Promise.all(
                    newestBudgetUses.map(async (budgetUse) => {
                        const product = await ProductClient.fetchProduct(budgetUse.account_id, budgetUse.product_id);
                        return product.type === ProductType.EQUIPMENT ? null : budgetUse;
                    })
                )
            ).filter((budgetUse): budgetUse is BudgetUse => budgetUse !== null);

            if (returnableBudgetUses.length === 0) return { count: 0, results: [] };

            const allowances = await AllowanceClient.postGetUserAllowancesV2(
                returnableBudgetUses.map((bu) => bu.product_id),
                {
                    spot_id: navigationState.spot?.id ?? '',
                    type: BudgetType.REPLENISHMENT,
                    user_id: userOverwrite?.id ?? undefined
                },
                navigationState.user?.auth
            );

            const result = returnableBudgetUses.map((bu) => ({ ...bu, ...allowances[bu.product_id] }));
            return {
                count: result.length,
                results: result
            };
        } catch (e) {
            // Usually happens when you dont have the right to fetch the allowance/budgetUse of the overwrite user. In that case we just dont show any vending products instead of showing an error.
            Logger.error('Failed to fetch budget uses or allowances for return overview screen', e);
            return { count: 0, results: [] };
        }
    };

    return {
        isLoading,
        setIsLoading,
        fetchData,
        onClick
    };
};

export default useReturnOverview;
